import { MoreHorizontal, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useUser } from '@/modules/common/application/context/user';
import { ProjectRole } from '@/modules/common/types';

import { useProjects } from '../hooks/use-projects';
import { ListProjectMember } from '../types/ProjectMember';
import { AddProjectMemberDialog } from './AddProjectMemberDialog';
import { ProjectRoleSelect } from './ProjectRoleSelect';

interface ProjectMembersTabProps {
  projectId: string;
  isOwner: boolean;
}

export const ProjectMembersTab = ({ projectId, isOwner }: ProjectMembersTabProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ListProjectMember | null>(null);

  const {
    getProjectMembers,
    updateMemberRole,
    removeMember,
    removeProjectMemberLoading,
    inviteProjectMembersLoading,
  } = useProjects();

  const members = getProjectMembers(projectId);

  const filteredMembers = members.filter((member) => {
    const searchLower = searchQuery.toLowerCase();
    const fullName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim();
    return (
      member.user?.email.toLowerCase().includes(searchLower) ||
      fullName.toLowerCase().includes(searchLower)
    );
  });

  const getInitials = (firstName?: string, lastName?: string, email?: string) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || email?.charAt(0).toUpperCase() || '?';
  };

  const getMemberDisplayName = (member: ListProjectMember) => {
    if (member.user?.firstName && member.user?.lastName) {
      return `${member.user?.firstName} ${member.user?.lastName}`;
    }
    return member.user?.email;
  };

  const getRoleDisplayName = (role: ProjectRole) => {
    switch (role) {
      case ProjectRole.OWNER:
        return t('modules.mavue2.project.roles.owner.label');
      case ProjectRole.MEMBER:
        return t('modules.mavue2.project.roles.member.label');
      default:
        return role;
    }
  };

  const handleRoleChange = async ({
    memberId,
    newRole,
  }: {
    memberId: string;
    newRole: ProjectRole;
  }) => {
    try {
      await updateMemberRole({ projectId, memberId, newRole });
    } catch {
      // Error handling is done in the hook
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      await removeMember({ projectId, memberId: memberToRemove.id });
      setMemberToRemove(null);
    } catch {
      // Error handling is done in the hook
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Header with search and invite */}
        <div className="flex items-center justify-between">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('modules.mavue2.project.members.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {isOwner && (
            <Button
              onClick={() => setShowInviteDialog(true)}
              disabled={inviteProjectMembersLoading}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('modules.mavue2.project.members.inviteMember')}
            </Button>
          )}
        </div>

        {/* Members List */}
        <div className="space-y-3">
          {filteredMembers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery
                ? t('modules.mavue2.project.members.noMembersFoundSearch')
                : t('modules.mavue2.project.members.noMembersFound')}
            </div>
          ) : (
            filteredMembers.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center space-x-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-500 text-white text-xs">
                      {getInitials(
                        member.user?.firstName || '',
                        member.user?.lastName || '',
                        member.user?.email || ''
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">{getMemberDisplayName(member)}</span>
                      {member.id === user?.id && (
                        <Badge variant="secondary" className="text-xs">
                          {t('modules.mavue2.settings.members.you')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{member.user?.email}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {isOwner || member.id === user?.id ? (
                    <ProjectRoleSelect
                      value={member.role}
                      onValueChange={(value: ProjectRole) =>
                        handleRoleChange({
                          memberId: member.id,
                          newRole: value,
                        })
                      }
                      className="w-[200px]"
                    />
                  ) : (
                    <div className="w-[200px] text-sm font-medium">
                      {getRoleDisplayName(member.role)}
                    </div>
                  )}

                  {/* Actions Menu */}
                  {isOwner && member.id !== user?.id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => setMemberToRemove(member)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('modules.mavue2.settings.members.remove')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <AddProjectMemberDialog
        projectId={projectId}
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
      />

      {/* Remove Member Confirmation Dialog */}
      <Dialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('modules.mavue2.project.members.removeMember')}</DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="modules.mavue2.project.members.removeMemberDescription"
                values={{
                  memberName: memberToRemove && getMemberDisplayName(memberToRemove),
                }}
                components={{ strong: <span className="font-medium" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberToRemove(null)}>
              {t('modules.mavue2.common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveMember}
              disabled={removeProjectMemberLoading}
            >
              {removeProjectMemberLoading && <Spinner className="mr-2" />}
              {t('modules.mavue2.project.members.removeMember')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
